// The sign-off queue at the top of the home Dashboard (prototype §45 dirNow): the one place on the page where the
// director does something. Bills over the limit (sign off, or pay through the usual dialog), AI actions (approve /
// turn down), draft owner statements (issue), contract notices (open). What shows is what this user may approve.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bot, FileCheck2, FileSignature, ReceiptText } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { billsApi } from '../../lib/api';
import { directorApi, fmtMoney, type NeedsMe, type NeedsMeItem } from '../../lib/director';
import { Confirm } from '../forms/common';
import { BillPayDialog } from '../forms/BillPayDialog';
import type { Bill } from '../../types/api';

const ICON = { bill: ReceiptText, aiAction: Bot, ownerStatement: FileCheck2, contract: FileSignature } as const;
const KIND = { bill: 'Bill to sign off', aiAction: 'AI action', ownerStatement: 'Owner statement', contract: 'Contract notice' } as const;

type Act = { kind: 'signOff' | 'approveAi' | 'rejectAi' | 'issue'; item: NeedsMeItem };

/** `highlightId` (from ?ai=<id>) rings that one item — the link an AI action notification opens. */
export function NeedsMeQueue({ needs, onNotice, highlightId }: { needs: NeedsMe; onNotice: (m: string) => void; highlightId?: string | null }) {
  const qc = useQueryClient();
  const canPay = useCan('bills.pay');
  const canReject = useCan('aiActions.reject');
  const [act, setAct] = useState<Act | null>(null);
  const [paying, setPaying] = useState<Bill | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['director'] }); void qc.invalidateQueries({ queryKey: ['home-blocks'] }); };

  const run = useMutation({
    mutationFn: async ({ a, reason }: { a: Act; reason: string }) => {
      if (a.kind === 'signOff') return directorApi.signOff(a.item.id, reason || undefined);
      if (a.kind === 'approveAi') return directorApi.approveAi(a.item.id);
      if (a.kind === 'rejectAi') return directorApi.rejectAi(a.item.id, reason);
      return directorApi.issueStatement(a.item.id);
    },
    onSuccess: (_r, { a }) => {
      setAct(null); refresh();
      onNotice(a.kind === 'signOff' ? 'Signed off — accounts can pay it.' : a.kind === 'approveAi' ? 'Approved — the action has run.' : a.kind === 'rejectAi' ? 'Turned down.' : 'Statement issued — the figures are locked.');
    },
  });
  const openPay = useMutation({ mutationFn: (id: string) => billsApi.get(id), onSuccess: (b) => setPaying(b) });

  const items = [...needs.billsAwaitingSignoff, ...needs.aiActionsPending, ...needs.ownerStatementsDraft, ...needs.contractsNoticeDue];
  const btn = 'inline-flex h-7 items-center rounded-full px-3 text-[12px] font-medium transition';

  return (
    <section className="surface overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ink-100 px-4 py-2.5">
        <h2 className="text-[13px] font-semibold text-ink-900">What needs me <span className="ml-1 rounded-full bg-violet-100 px-2 py-0.5 align-middle text-[12px] font-semibold text-violet-700">{needs.total}</span></h2>
        <span className="text-[11.5px] text-ink-400">Approving releases a bill to accounts to pay. Nothing over the limit leaves the account without it.</span>
      </div>
      {!items.length ? <p className="px-5 py-8 text-center text-[13px] text-ink-500">Nothing is waiting on you. Anything over the sign-off limit will appear here the moment it is approved.</p> : (
        <ul className="divide-y divide-ink-100">
          {items.map((it) => {
            const Icon = ICON[it.kind];
            return (
              <li key={`${it.kind}:${it.id}`} id={`needs-${it.id}`} aria-current={highlightId === it.id ? 'true' : undefined}
                className={`flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5 ${highlightId === it.id ? 'bg-bronze-50 ring-2 ring-inset ring-bronze-400' : ''}`}>
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-violet-50 text-violet-600"><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium text-ink-500">{KIND[it.kind]}{it.date ? ` · ${it.date}` : ''}</div>
                    <div className="truncate text-[13.5px] font-medium text-ink-900">{it.title}</div>
                    <div className="text-[12px] text-ink-500">{it.detail}</div>
                  </div>
                </div>
                {it.amountMinor !== null && it.kind !== 'ownerStatement' && <div className="text-[14px] font-semibold tabular-nums text-ink-900 sm:w-28 sm:text-right">{fmtMoney(it.amountMinor, it.currency ?? 'GBP')}</div>}
                <div className="flex flex-wrap gap-1.5">
                  {it.kind !== 'aiAction' && <Link to={it.url} className={`${btn} border border-ink-200 bg-ink-100 text-ink-800 hover:bg-ink-200`}>Look at it</Link>}
                  {it.kind === 'bill' && <>
                    {canPay && <button type="button" className={`${btn} border border-ink-200 bg-ink-100 text-ink-800 hover:bg-ink-200`} onClick={() => openPay.mutate(it.id)}>Sign off & pay</button>}
                    <button type="button" className={`${btn} bg-bronze-500 text-on-primary hover:bg-bronze-600`} onClick={() => setAct({ kind: 'signOff', item: it })}>Sign off</button>
                  </>}
                  {it.kind === 'aiAction' && <>
                    {canReject && <button type="button" className={`${btn} border border-red-200 text-red-600 hover:bg-red-50`} onClick={() => setAct({ kind: 'rejectAi', item: it })}>Turn down</button>}
                    <button type="button" className={`${btn} bg-bronze-500 text-on-primary hover:bg-bronze-600`} onClick={() => setAct({ kind: 'approveAi', item: it })}>Approve</button>
                  </>}
                  {it.kind === 'ownerStatement' && <button type="button" className={`${btn} bg-bronze-500 text-on-primary hover:bg-bronze-600`} onClick={() => setAct({ kind: 'issue', item: it })}>Issue</button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Confirm open={!!act} onClose={() => setAct(null)} danger={act?.kind === 'rejectAi'}
        title={act?.kind === 'signOff' ? `Sign off ${act.item.amountMinor !== null ? fmtMoney(act.item.amountMinor, act.item.currency ?? 'GBP') : ''}?` : act?.kind === 'approveAi' ? 'Approve this AI action?' : act?.kind === 'rejectAi' ? 'Turn this AI action down?' : 'Issue this statement?'}
        body={<div className="space-y-1 text-[13px] text-ink-600"><p className="font-medium text-ink-900">{act?.item.title}</p><p>{act?.item.detail}</p>
          {act?.kind === 'signOff' && <p className="pt-2">Accounts can pay it once you do. Nothing is paid now.</p>}
          {act?.kind === 'approveAi' && <p className="pt-2">It runs now, as you, and is recorded against your name.</p>}
          {act?.kind === 'issue' && <p className="pt-2">The figures are locked and the draft mark comes off. Sending it to the investors is a separate step.</p>}</div>}
        confirmLabel={act?.kind === 'signOff' ? 'Sign off' : act?.kind === 'approveAi' ? 'Approve' : act?.kind === 'rejectAi' ? 'Turn it down' : 'Issue'}
        reasonLabel={act?.kind === 'rejectAi' ? 'Why are you turning it down?' : undefined}
        pending={run.isPending} error={run.error} onConfirm={(reason) => act && run.mutate({ a: act, reason })} />
      {paying && <BillPayDialog open onClose={() => setPaying(null)} bill={paying} onPaid={(m) => { setPaying(null); refresh(); onNotice(m); }} />}
    </section>
  );
}
